import { DateTime } from 'luxon';
import { makeAutoObservable } from 'mobx';
import { LoanTransactionApiResponse, LoanTransactionDetailsApiResponse, LoanTransactionMatchApiResponse } from '../../api/Loans';
import { SourceType } from '../../api/SourceApi';
import { LoanTransactionType } from '../../config/Constants';
import { convertCurrencyAtDate } from '../../utils/Numbers';
import { Loan } from './Loan';
import { Transaction } from './Transaction';

export class OrgTransactions {
  id: number = 0;
  amount: number = 0;
  balance: number = 0;
  _transactions: Transaction[] = null;
  rawTransactions: LoanTransactionApiResponse[] = [];

  constructor(orgId: number, amount: number, balance: number, transactions: LoanTransactionApiResponse[]) {
    makeAutoObservable(this);
    this.id = orgId;
    this.amount = amount;
    this.balance = balance;
    this.rawTransactions = transactions;
  }

  get transactions() {
    if (this._transactions == null) {
      this._transactions = this.rawTransactions.map(t => {
        const transaction = Transaction.fromLoansApi(t);
        transaction.source = SourceType.xero;
        transaction.organisationId = this.id;
        return transaction;
      });
    }
    return this._transactions;
  }

  set transactions(newTxs: Transaction[]) {
    this._transactions = newTxs;
  }

}

export class LoanTransactionDetails {
  loan: Loan = null;
  date: DateTime;
  type: LoanTransactionType = null;
  variance: number = 0;

  org1: OrgTransactions = null;
  org2: OrgTransactions = null;
  
  highlight: boolean = false;
  persisted: boolean = true;
  userMatched: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(loanDetails: LoanTransactionDetailsApiResponse, apiData: LoanTransactionMatchApiResponse, loan: Loan) {
    const details = new LoanTransactionDetails();

    details.loan = loan;

    details.date = DateTime.fromISO(apiData.date);
    details.type = apiData.type as LoanTransactionType;

    const apiOrg1: OrgTransactions = new OrgTransactions(
      loanDetails.org1Id,
      apiData.org1Amount,
      apiData.org1Balance,
      apiData.org1Transactions
    );

    const apiOrg2: OrgTransactions = new OrgTransactions(
      loanDetails.org2Id,
      apiData.org2Amount,
      apiData.org2Balance,
      apiData.org2Transactions
    );

    if (apiOrg1.id === loan.lender.id) {
      details.org1 = apiOrg1;
      details.org2 = apiOrg2;
    } else {
      details.org1 = apiOrg2;
      details.org2 = apiOrg1;
    }

    details.userMatched = apiData.userMatched;

    return details;
  }

  toApi = (): LoanTransactionMatchApiResponse => {
    return {
      date: this.date.toISO({ suppressMilliseconds: true, includeOffset: false }),
      type: this.type,
      userMatched: this.userMatched,
      org1Transactions: this.org1.transactions.map(t => ({ transactionId: t.id })) as LoanTransactionApiResponse[],
      org2Transactions: this.org2.transactions.map(t => ({ transactionId: t.id })) as LoanTransactionApiResponse[],
      org1Amount: 0,
      org2Amount: 0,
      org1Balance: 0,
      org2Balance: 0, // TODO: Ignore these?
    };
  };

  get lenderBalance(): number {
    return this.org1.balance;
  }

  get borrowerBalance(): number {
    return this.org2.balance;
  }

  get borrowerBalanceFx(): number {
    return convertCurrencyAtDate(this.org2.balance, this.loan.org2.currency, this.loan.org1.currency, this.date, this.loan.exchangeRates);
  }

  get lenderAmount(): number {
    return this.org1.amount;
  }

  get borrowerAmount(): number {
    return this.org2.amount;
  }

  get lenderTransactions(): Transaction[] {
    return this.org1.transactions;
  }

  get borrowerTransactions(): Transaction[] {
    return this.org2.transactions;
  }

  get id(): string {
    return `${this.org1.transactions.map(t => t.id).join(',')}:${this.org2.transactions.map(t => t.id).join(',')}`;
  }

  /**
   * Error is calculated by this formula:
   *  lenderBalance + borrowerBalance + error = 0
   * lenderBalance & borrowerBalance should be opposite signs
   */
  get error(): number {
    return -this.lenderBalance - this.borrowerBalanceFx;
  }

  matchesSearch = (value: string): boolean => {
    return this.org1.transactions.some(tx => tx.matchesSearch(value)) || this.org2.transactions.some(tx => tx.matchesSearch(value));
  };
}
